import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@clarix/auth-store';
import { aiConnectionsApi } from './ai-connections.api';

/**
 * React Query hooks over AI app connections. Keys are rooted at
 * `['ai-connections', slug]` so a disconnect from the profile and a revoke from
 * the owner tab refresh each other. Mutations stay toast-free; callers own copy.
 */

const aiConnectionsRoot = (slug: string | null) => ['ai-connections', slug] as const;

/** The caller's own grants, their workspace MCP URL and the workspace switch. */
export function useMyAiConnections() {
  const slug = useAuthStore((s) => s.workspaceSlug);
  return useQuery({
    queryKey: [...aiConnectionsRoot(slug), 'mine'],
    queryFn: () => aiConnectionsApi.listMine(),
    enabled: !!slug,
    // `last_used_at` moves while the member's AI app works.
    staleTime: 30_000,
  });
}

/**
 * Every member's grants. `enabled` lets the owner screen skip the call when
 * `manage_api_keys` is already known to be missing, as `useApiTokens` does.
 */
export function useWorkspaceAiConnections(options: { enabled?: boolean } = {}) {
  const slug = useAuthStore((s) => s.workspaceSlug);
  return useQuery({
    queryKey: [...aiConnectionsRoot(slug), 'workspace'],
    queryFn: () => aiConnectionsApi.listWorkspace(slug as string),
    enabled: !!slug && (options.enabled ?? true),
    staleTime: 30_000,
  });
}

function useInvalidateAiConnections() {
  const qc = useQueryClient();
  const slug = useAuthStore((s) => s.workspaceSlug);
  return () => qc.invalidateQueries({ queryKey: aiConnectionsRoot(slug) });
}

export function useDisconnectAiConnection() {
  const invalidate = useInvalidateAiConnections();
  return useMutation({
    mutationFn: (id: string) => aiConnectionsApi.disconnectMine(id),
    onSuccess: invalidate,
  });
}

export function useRevokeWorkspaceAiConnection() {
  const slug = useAuthStore((s) => s.workspaceSlug);
  const invalidate = useInvalidateAiConnections();
  return useMutation({
    mutationFn: (id: string) => aiConnectionsApi.revokeWorkspace(slug as string, id),
    onSuccess: invalidate,
  });
}
